import clsx from 'clsx';
import { KeyboardEvent, MouseEvent, useCallback, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import { KeyCode, MIME_TYPE_IMAGE_JPEG, MIME_TYPE_VIDEO_MP4 } from '@vestico/utils';

import { MediaDisplay } from '@vestico/shared';

import { LikePreview } from '../LikePreview';
import { PreviewText } from '../PreviewText';
import { BaseItemProps } from '../model/item';
import { getResponseElementID } from '../model/layoutHelpers';
import style from './FeedItem.module.css';

export const FEED_LAYOUT_ITEM_BORDER_RADIUS_KEY = '--vw-feedlayout-item-border-radius';

export interface FeedItemProps extends BaseItemProps {
  index: number;
  onClick: (index: number) => void;
}

export const FeedItem = ({ response, index, onClick, previewText, showLikes, aspectRatio, playVideo, updateInView }: FeedItemProps) => {
  const { ref, inView } = useInView({ threshold: 0.5 });
  const { userName, media } = response;
  const { cropHints, mainMimeType, imageSources, videoPreviews } = media;
  const jpegSources = imageSources[MIME_TYPE_IMAGE_JPEG];
  const cropHint = useMemo(() => cropHints && aspectRatio && cropHints[aspectRatio], [cropHints, aspectRatio]);

  useEffect(() => {
    if (updateInView) {
      updateInView(inView);
    }
  }, [updateInView, inView]);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      onClick(index);
    },
    [onClick, index]
  );

  const handleKeyUp = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (e.code === KeyCode.enter) {
        onClick(index);
      }
    },
    [index, onClick]
  );

  const borderRadius = [`var(${FEED_LAYOUT_ITEM_BORDER_RADIUS_KEY})`];

  return (
    <div
      className={clsx(style.root)}
      onClick={handleClick}
      onKeyUp={handleKeyUp}
      id={getResponseElementID(response)}
      role="button"
      tabIndex={0}
      ref={ref}
    >
      <div className={clsx(style.content)}>
        <div className={clsx(style.background)} />
        <MediaDisplay
          thumbURL={jpegSources?.[180]}
          imageURL={jpegSources[720]}
          videoURL={videoPreviews?.[MIME_TYPE_VIDEO_MP4]?.[640]}
          srcSet={imageSources}
          mimeType={mainMimeType}
          borderRadius={borderRadius}
          cropHint={cropHint}
          loadInView
          animate
          playVideo={playVideo}
          disableVideoControls
        />
      </div>
      <div className={clsx(style.bottom)}>
        {showLikes ? <LikePreview response={response} /> : null}
        <PreviewText userName={userName} content={previewText} />
      </div>
    </div>
  );
};
